import React, { useMemo, useState, useEffect, forwardRef,
  useImperativeHandle } from "react";
import { useTranslation } from "../../../../../i18n/useTranslation";
import {
  View,
  Text,
  StyleSheet,
  Platform,
} from "react-native";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import FloatingTextInputComponent from "../../../common/FloatingTextInputComponent";
import { capturePosthogEvent } from "../../../../../utils/analytics";
import {
  IDENTIFICATION_NUMBER_MAX_LENGTH,
  isIdentificationNumberContentValid,
  sanitizeIdentificationNumberInput,
} from "../../../../../helper/identificationNumberValidation";

type Props = {
  onValidationChange?: (isValid: boolean) => void;
  testID?: string | undefined;
};

export interface SenderIdentificationStepHandle {
  submit: () => Promise<boolean>;
}

const SenderIdentificationNumber = forwardRef<SenderIdentificationStepHandle, Props>(({ onValidationChange, testID }: Props, ref) => {
  const { t, i18n } = useTranslation();
  const [errors, setErrors] = useState({
      idNumber: "",
  });
  const storedPhotoIdNumber = useSessionStore((s: SessionState) => (s as any).senderPhotoIdNumber as string | null | undefined);
  const [idNumber, setIdNumber] = useState(() => {
    const raw = (useSessionStore.getState() as any).senderPhotoIdNumber as string | null | undefined;
    const cleaned = sanitizeIdentificationNumberInput(String(raw ?? ""));
    return cleaned.toUpperCase();
  });
  const selectedIdTypeKey = useSessionStore((s: SessionState) => (s as any).selectedIdTypeKey);
  const selectedIdTypeLabel = useSessionStore((s: SessionState) => (s as any).selectedIdTypeLabel);
  const setSenderPhotoIdNumber = useSessionStore((s: SessionState) => (s as any).setSenderPhotoIdNumber);

  useEffect(() => {
    const cleaned = sanitizeIdentificationNumberInput(String(storedPhotoIdNumber ?? ""));
    setIdNumber(cleaned.toUpperCase());
  }, [storedPhotoIdNumber]);

  const placeholderText = useMemo(() => {
    const baseLabel = selectedIdTypeLabel || (i18n.language === 'es' ? t('US_DRIVERS_LICENSE_NUMBER') : t('US_DRIVERS_LICENSE_NUMBER'));
    const suffix = i18n.language === 'es' ? 'Número' : 'Number';
    return `${baseLabel} ${suffix}`;
  }, [selectedIdTypeLabel, i18n.language, t]);

  const isValid =
    idNumber.trim().length > 0 && isIdentificationNumberContentValid(idNumber);
      
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);

  // expose validate function
    useImperativeHandle(ref, () => ({
      submit: async () => {
        const trimmed = idNumber.trim();
        const newErrors = {
          idNumber: !trimmed
            ? "Identification number is required"
            : trimmed.length > IDENTIFICATION_NUMBER_MAX_LENGTH
              ? t("PERSON_NAME_ERROR_MAX_CHARS", { count: IDENTIFICATION_NUMBER_MAX_LENGTH })
              : !isIdentificationNumberContentValid(idNumber)
                ? t("ID_NUMBER_INVALID_CHARS")
                : "",
        };
        setErrors(newErrors);
    
        // if any error string is non-empty
        const isValid = !Object.values(newErrors).some(Boolean);
        return isValid;
      },
    }));


  const handleManualInput = async (text: string) => {
    const cleaned = sanitizeIdentificationNumberInput(text);
    const upper = cleaned.toUpperCase();
    setIdNumber(upper);
    try { setSenderPhotoIdNumber?.(upper); } catch {}
    if (errors.idNumber) setErrors({ ...errors, idNumber: "" });
  };

  const formatDate = (date: Date) => {
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  const TITLE_BLOCK_HEIGHT = 98;
  const TITLE_TO_INPUT_GAP = 32; // extra space so input sits clearly below title container

  return (
    <View style={styles.container} testID={testID}>
      <View style={[styles.headerWrap, Platform.OS === 'web' && styles.headerWrapFixedWeb]}>
        {i18n.language === 'en' ? (
          <Text style={styles.title}>
            Your identification {"\n"}number
          </Text>
        ) : (
          <Text style={styles.title}>
            {t("SENDER_IDENTIFICATION_NUMBER")}
          </Text>
        )}
        <Text style={styles.subtitle}>{t("PROVIDE_YOUR_PHOTO_ID_NUMBER")}</Text>
      </View>
      <View style={Platform.OS === 'web' ? { paddingTop: TITLE_BLOCK_HEIGHT + TITLE_TO_INPUT_GAP } : undefined}>
      <FloatingTextInputComponent
        testID="senderIdentificationNumber"
        isAutoFocus={true}
        label={placeholderText}
        value={idNumber}
        onChangeText={handleManualInput}
        errorText={errors.idNumber}
        onClearError={() => setErrors((prev) => ({ ...prev, idNumber: "" }))}
        autoCapitalize="characters"
        maxLength={IDENTIFICATION_NUMBER_MAX_LENGTH}
      />
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 24,
    backgroundColor: Colors.white,
    paddingBottom: 200,
    ...(Platform.OS === 'web' && { overflow: 'visible' as any }),
  },
  headerWrap: {
    flexShrink: 0,
  },
  /** Web: fixed so title stays at top (step is rendered outside ScrollView in stepAreaContainer). */
  headerWrapFixedWeb: {
    position: 'fixed' as any,
    top: 80 as any,
    left: 24 as any,
    right: 24 as any,
    zIndex: 5 as any,
    backgroundColor: Colors.white,
    paddingBottom: 8,
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8,
    // paddingRight: 100
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 24
  },
  inputWrapper: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#f5f5f5",
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 400
  },
  input: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: "400",
    lineHeight: 24,
    color: Colors.subTitleColor,
  },
  modalOverlay: {
    flex: 1,
    justifyContent: "flex-end",
    backgroundColor: "rgba(0,0,0,0.4)",
  },
  bottomSheet: {
    backgroundColor: "#fff",
    padding: 20,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  modalTitle: { fontSize: 18, fontWeight: "700", marginBottom: 10 },
  modalText: { fontSize: 15, color: "#555" },
  modalDOB: {
    fontSize: 18,
    fontWeight: "600",
    marginTop: 10,
    marginBottom: 20,
    textAlign: "center",
  },
  confirmBtn: {
    backgroundColor: "#000",
    padding: 14,
    borderRadius: 8,
    alignItems: "center",
  },
  confirmText: { color: "#fff", fontWeight: "600", fontSize: 16 },
  cancelBtn: { marginTop: 10, alignItems: "center" },
  cancelText: { fontSize: 15, color: "#666" },
});

export default SenderIdentificationNumber;